import { useMemo } from 'react'
import {
    DetalizationType,
    UsersSortType,
    TrafficSourceType,
} from '@/features/analytics/constants'
import { useInviteLinksStore } from '@/features/analytics/model'
import { LegendType } from 'recharts'
import { generateUniqueColor } from '@/features/analytics/utils'
import {
    InviteLinkWithAnalyticsType,
    useGetGroupsByChannelIdQuery,
} from '@/shared/api'
import { useParams } from 'react-router'

interface RetentionDataPoint {
    index: number
    [key: string]: number
}
export const useRetentionGroupsChartData = (channelId: number) => {
    const { groupId } = useParams<{ groupId: string }>()
    const numericGroupId = Number(groupId)
    const {
        selectedRetentionGroups,
        trafficSourceSortRetentionGroup,
        detalizationSortRetentionGroup,
        usersSortRetentionGroup,
        startDateRetentionGroup,
        endDateRetentionGroup,
    } = useInviteLinksStore()

    const { data: allGroups = [] } = useGetGroupsByChannelIdQuery(channelId)

    const selectedGroupsIds = selectedRetentionGroups[numericGroupId] || []
    const selectedGroups = allGroups.filter((group) =>
        selectedGroupsIds.includes(group.invite_links_group_id)
    )

    const retentionData = useMemo(() => {
        if (selectedGroups.length === 0) return []

        const dateIndices = new Set<number>()

        const groupRetentionDataMap = new Map<number, Map<number, number>>()

        selectedGroups.forEach((group) => {
            const allInviteLinks = group.inviteLinks

            const filteredLinks = filterLinks(
                allInviteLinks,
                trafficSourceSortRetentionGroup.value
            )

            const sortedData = sortData(
                filteredLinks,
                detalizationSortRetentionGroup.value,
                startDateRetentionGroup,
                endDateRetentionGroup
            )

            const groupRetentionData = calculateGroupRetentionData(
                sortedData,
                usersSortRetentionGroup.value,
                detalizationSortRetentionGroup.value
            )

            // groupRetentionData.forEach((dataPoint) => {
            //     dateIndices.add(dataPoint.index)
            // })
            //
            // groupRetentionDataMap.set(
            //     group.invite_links_group_id,
            //     groupRetentionData
            // )

            groupRetentionData.forEach((dataPoint) => {
                dateIndices.add(dataPoint.index)
                if (!groupRetentionDataMap.has(group.invite_links_group_id)) {
                    groupRetentionDataMap.set(
                        group.invite_links_group_id,
                        new Map()
                    )
                }
                groupRetentionDataMap
                    .get(group.invite_links_group_id)!
                    .set(dataPoint.index, dataPoint.retentionRate)
            })
        })

        const combinedRetentionData: RetentionDataPoint[] = Array.from(
            dateIndices
        )
            .sort((a, b) => a - b)
            .map((index) => {
                const dataPoint: RetentionDataPoint = { index }
                groupRetentionDataMap.forEach((groupData, groupId) => {
                    const value = groupData.get(index)
                    if (value !== undefined) {
                        dataPoint[groupId.toString()] = value
                    }
                })
                return dataPoint
            })

        return combinedRetentionData
    }, [
        selectedGroups,
        trafficSourceSortRetentionGroup.value,
        detalizationSortRetentionGroup.value,
        startDateRetentionGroup,
        endDateRetentionGroup,
        usersSortRetentionGroup.value,
    ])

    const groups = useMemo(() => {
        const selectedGroupsIds = selectedRetentionGroups[numericGroupId] || []
        const selectedGroups = allGroups.filter((group) =>
            selectedGroupsIds.includes(group.invite_links_group_id)
        )

        return selectedGroups.map((group) => ({
            id: group.invite_links_group_id,
            name: group.group_name,
            value: group.group_name,
            type: 'line' as LegendType,
            color: generateUniqueColor(group.invite_links_group_id),
        }))
    }, [allGroups, numericGroupId, selectedRetentionGroups])

    const lastIndices = useMemo(() => {
        const indices: Record<string, number> = {}
        groups.forEach((link) => {
            const dataKey = link.id
            let lastIndex = -1
            retentionData.forEach((entry, idx) => {
                if (entry[dataKey] != null) {
                    lastIndex = idx
                }
            })
            indices[dataKey] = lastIndex
        })
        return indices
    }, [groups, retentionData])

    return { retentionData, groups, lastIndices }
}

const filterLinks = (
    links: InviteLinkWithAnalyticsType[],
    trafficSource: TrafficSourceType
): InviteLinkWithAnalyticsType[] => {
    return links.filter((link) => {
        const isInviteLink = link.invite_link_id !== undefined

        if (trafficSource !== 'allTraffic') {
            if (
                (trafficSource === 'inviteLinks' && !isInviteLink) ||
                (trafficSource === 'anySource' && isInviteLink)
            ) {
                return false
            }
        }

        return true
    })
}

const sortData = (
    links: InviteLinkWithAnalyticsType[],
    detalization: DetalizationType,
    startDate: string,
    endDate: string
): Map<string, InviteLinkWithAnalyticsType[]> => {
    const start = new Date(startDate)
    const end = new Date(endDate)

    const filteredLinks = links.filter((link) => {
        const linkDate = new Date(link.created_at)
        return linkDate >= start && linkDate <= end
    })

    const groupedData = new Map<string, InviteLinkWithAnalyticsType[]>()

    filteredLinks.forEach((link) => {
        const date = new Date(link.created_at)
        let key: string

        const weekStart = new Date(date.setDate(date.getDate() - date.getDay()))

        switch (detalization) {
            case 'daily':
                key = date.toISOString().split('T')[0]
                break
            case 'weekly':
                key = weekStart.toISOString().split('T')[0]
                break
            case 'monthly':
                key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
                break
            default:
                key = date.toISOString().split('T')[0]
        }

        if (!groupedData.has(key)) {
            groupedData.set(key, [])
        }
        groupedData.get(key)!.push(link)
    })

    return new Map([...groupedData.entries()].sort())
}

const calculateGroupRetentionData = (
    sortedData: Map<string, InviteLinkWithAnalyticsType[]>,
    usersSortRetention: UsersSortType,
    detalization: DetalizationType
): { index: number; retentionRate: number }[] => {
    const retentionData: { index: number; retentionRate: number }[] = [
        { index: 0, retentionRate: 100 },
    ]
    let initialSubscribers = 0

    sortedData.forEach((links) => {
        links.forEach((link) => {
            if (usersSortRetention === 'allUsers') {
                initialSubscribers += link.total_subscribers
            } else {
                initialSubscribers += link.total_premium_subscribers
            }
        })
    })

    let currentSubscribers = initialSubscribers

    let maxAnalyticsLength = 0
    sortedData.forEach((links) => {
        links.forEach((link) => {
            if (link.analytics && link.analytics.length > maxAnalyticsLength) {
                maxAnalyticsLength = link.analytics.length
            }
        })
    })

    for (let index = 1; index <= maxAnalyticsLength; index++) {
        if (
            (detalization === 'weekly' && index % 7 === 0) ||
            (detalization === 'monthly' && index % 30 === 0) ||
            detalization === 'daily'
        ) {
            const dataPointIndex = Math.floor(
                index /
                    (detalization === 'weekly'
                        ? 7
                        : detalization === 'monthly'
                          ? 30
                          : 1)
            )

            let unsubscribedThisInterval = 0

            sortedData.forEach((links) => {
                links.forEach((link) => {
                    if (!link.analytics) return

                    const analytics = link.analytics[index - 1]
                    if (analytics) {
                        if (usersSortRetention === 'allUsers') {
                            unsubscribedThisInterval +=
                                analytics.unsubscribed_count
                        } else {
                            unsubscribedThisInterval +=
                                analytics.premium_unsubscribed_count
                        }
                    }
                })
            })

            currentSubscribers -= unsubscribedThisInterval

            const retentionRate = initialSubscribers
                ? (currentSubscribers / initialSubscribers) * 100
                : 0

            retentionData[dataPointIndex] = {
                index: dataPointIndex,
                retentionRate,
            }
        }
    }

    return retentionData
}
